import { matchDesktop, isDesktop } from './media-query';

/**
 * Initialize functions for the fixed navigation.
 */
const initFixedNav = () => {
  const { body } = document;
  const fixedElements = document.querySelectorAll('.js-fixed');
  const offsetY = window.pageYOffset || document.body.scrollTo;

  if (fixedElements.length) {
    if (typeof offsetY === 'number' && offsetY > 36) {
      fixedElements.forEach((element) => element.classList.add('is-fixed'));
      body.classList.add('js-nav-fixed', 'nav-fixed');
    } else {
      fixedElements.forEach((element) => element.classList.remove('is-fixed'));
      body.classList.remove('js-nav-fixed', 'nav-fixed');
    }
  }
};

const closeTopDropdown = () => {
  const topElement = document.querySelector('.top');
  if (topElement) {
    const toggleWrap = topElement.querySelectorAll('.js-toggle-wrap');
    if (toggleWrap.length) {
      toggleWrap.forEach((element) => {
        const id = element.getAttribute('data-toggleId');
        const target = document.getElementById(id);
        target.classList.remove('opened');
        element.classList.remove('active');
      });
    }
  }
};

const closeMegaMenu = () => {
  const megaMenu = document.getElementById('mega-menu');
  if (megaMenu) {
    const toggleWrap = megaMenu.querySelectorAll('.js-toggle-wrap');
    const slideMenu = megaMenu.querySelectorAll('.slide-menu');
    if (toggleWrap.length && slideMenu.length) {
      toggleWrap.forEach((element) => element.classList.remove('active'));
      slideMenu.forEach((element) => element.classList.remove('opened'));
    }
  }
};

const getToggleId = (button: Element) => button
  .closest('.js-toggle-wrap')
  .getAttribute('data-toggleId');

const removeActiveClassMainLinks = (triggerId: string) => {
  const navbarMainLinks = document.getElementById('navbar-main-links');
  if (navbarMainLinks) {
    const toggleWrap = navbarMainLinks.querySelectorAll('.js-toggle-wrap');

    if (toggleWrap.length) {
      toggleWrap.forEach((element) => {
        const elementToggleId = getToggleId(element);
        if (triggerId !== elementToggleId) {
          element.classList.remove('active');
        }
      });
    }
  }
};

/**
 * Navigation handler
 * 1. Controls mega menu active state and show mega menu items in slide
 * 2. Controls relation and logic between top section and navbar
 *  a) When user clicks on top section drop down hide mega menu
 *  b) When user clicks on main nav links hide top drop down items
 */
export default class Navigation {
  mainButtonLink = document.querySelectorAll('.js-main-link');

  topToggle = document.querySelectorAll('.top .js-top-toggle');

  mainLinkMobile = document.querySelectorAll('.js-main-link-mobile');

  megaMenuOpened = new Event('megaMenuOpened');

  topDropDownOpened = new Event('topDropDownOpened');

  constructor() {
    const matchDesktopdevice = matchDesktop();
    this.megaMenuLogic(isDesktop());

    if (this.mainButtonLink.length) {
      try {
        // Chrome & Firefox
        matchDesktopdevice.addEventListener('change', (media) => this.megaMenuLogic(media.matches));
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e1) {
        try {
          // Safari
          matchDesktopdevice.addListener((media) => this.megaMenuLogic(media.matches));
        } catch (e2) {

          console.error(e2);
        }
      }
    }

    if (this.topToggle.length) {
      this.topToggle.forEach((toggle) => {
        toggle.addEventListener('click', this.topDropdownHandler.bind(this));
      });
      this.topToggle.forEach((toggle) => {
        toggle.addEventListener('topDropDownOpened', closeMegaMenu.bind(this));
      });
    }

    window.addEventListener('scroll', () => initFixedNav());
  }

  megaMenuHandler(element: Event) {
    const trigger = (element.currentTarget) as HTMLElement;
    if (trigger) {
      const triggerId = getToggleId(trigger);
      if (triggerId) {
        removeActiveClassMainLinks(triggerId);
        const id = trigger.getAttribute('data-mega-menu');
        if (isDesktop()) {
          document.querySelectorAll('.slide-menu').forEach((slideMenu) => {
            slideMenu.classList.remove('opened');
          });
        }
        const megaMenuElement = document.getElementById(id);
        megaMenuElement.classList.add('opened');
        // Accesaibility focus to first element in mega menu
        megaMenuElement.addEventListener("transitionend", function focusAfterTransition(event) {
          if (event.propertyName === "opacity" || event.propertyName === "transform") {
            const focusableElements = Array.from(megaMenuElement.querySelectorAll('button, a')).filter((element) => {
              const htmlElement = element as HTMLElement;
              const style = window.getComputedStyle(htmlElement);
              const isVisible = style.display !== 'none' && style.visibility !== 'hidden' && htmlElement.offsetWidth > 0 && htmlElement.offsetHeight > 0;

              return isVisible;
            }
            );
            if (focusableElements.length > 0) {
              (focusableElements[0] as HTMLElement).focus();
            }
            megaMenuElement.removeEventListener("transitionend", focusAfterTransition);
          }
        });
        element.currentTarget.dispatchEvent(this.megaMenuOpened);
      }
    }
  }

  /**
   * Close all dropdown from top component except clicked one
   * @param element clikced js-toggle element
   */
  topDropdownHandler(element: Event) {
    const htmlElement = (element.currentTarget) as HTMLElement;
    const triggerWrapperId = htmlElement
      .closest('.js-toggle-wrap')
      .getAttribute('data-toggleId');
    if (triggerWrapperId) {
      this.topToggle.forEach((toggle) => {
        const toggleWrap = toggle.closest('.js-toggle-wrap');
        const topToggleId = toggleWrap.getAttribute('data-toggleId');
        if (topToggleId !== triggerWrapperId) {
          toggleWrap.classList.remove('active');
          document.getElementById(topToggleId).classList.remove('opened');
        }
      });
      element.currentTarget.dispatchEvent(this.topDropDownOpened);
    }
  }

  /**
   * Open dropdown and close other opened dropdowns/main link/mega-menu from main buttons
   * @param element main button icon on mobile device
   */
  mainLinkMobileHandler(element: Event) {
    const triggerId = getToggleId(element.currentTarget as HTMLElement);
    if (this.mainLinkMobile.length) {
      this.mainLinkMobile.forEach((mainLink) => {
        const toggleWrap = mainLink.closest('.js-toggle-wrap');
        const id = toggleWrap.getAttribute('data-toggleId');
        const overlay = document.getElementById(id);

        // click on language and search
        if (triggerId !== id && triggerId !== 'navbar-main-links') {
          toggleWrap.classList.remove('active');
          if (overlay) {
            overlay.classList.remove('opened');
          }

          closeMegaMenu();

          return;
        }

        // click on hamburger button only
        if (triggerId === 'navbar-main-links' && id !== 'navbar-main-links') {
          toggleWrap.classList.remove('active');

          if (overlay) {
            overlay.classList.remove('opened');
          }
        }
      });
    }
  }

  megaMenuLogic(isDesktopScreen: boolean) {
    if (isDesktopScreen) {
      this.mainLinkMobile.forEach((element) => element.removeEventListener('click', this.mainLinkMobileHandler.bind(this)));
      this.mainButtonLink.forEach((button) => button.addEventListener('click', this.megaMenuHandler.bind(this)));
      this.mainButtonLink.forEach((button) => button.addEventListener('megaMenuOpened', closeTopDropdown.bind(this)));
    } else {
      this.mainButtonLink.forEach((button) => button.removeEventListener('click', this.megaMenuHandler.bind(this)));
      this.mainLinkMobile.forEach((element) => element.addEventListener('click', this.mainLinkMobileHandler.bind(this)));
    }
  }
}
